import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Map as MapIcon,
  RestartAlt as RestartAltIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import geocodingMonitoringService from '../../services/geocodingMonitoringService';

const GeocodingMonitoringPage = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [providersStatus, setProvidersStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10000); // 10 secondes au lieu de 5
  const [testAddress, setTestAddress] = useState('Paris, France');
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const intervalRef = useRef(null);
  const monitorRef = useRef(null);

  // Charger les statistiques avec le service
  const loadStats = async () => {
    try {
      setError(null);
      const data = await geocodingMonitoringService.getAllMonitoringData();
      
      if (data.stats) {
        const formattedStats = geocodingMonitoringService.formatStatsForDisplay(data.stats);
        setStats(formattedStats);
        
        const metrics = geocodingMonitoringService.calculatePerformanceMetrics(data.stats);
        setPerformanceMetrics(metrics);
      }
      
      if (data.providers) {
        setProvidersStatus(data.providers);
      }
      
      if (data.errors.stats || data.errors.providers) {
        setError('Erreur partielle lors du chargement des données');
      }
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
      setError('Erreur lors du chargement des statistiques de géocodage');
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser les statistiques
  const resetStats = async () => {
    try {
      await geocodingMonitoringService.resetStats();
      await loadStats();
    } catch (err) {
      console.error('Erreur lors de la réinitialisation:', err);
      setError('Erreur lors de la réinitialisation des statistiques');
    }
  };

  // Test d'API
  const testGeocoding = async () => {
    try {
      setLoading(true);
      await geocodingMonitoringService.testGeocoding(testAddress);
      await loadStats();
    } catch (err) {
      console.error('Erreur lors du test:', err);
      setError('Erreur lors du test de géocodage');
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh avec monitoring service optimisé
  useEffect(() => {
    if (autoRefresh) {
      monitorRef.current = geocodingMonitoringService.createRealTimeMonitor(
        (data) => {
          if (data.error) {
            setError(data.error);
          } else {
            // Mise à jour des stats si disponibles
            if (data.stats) {
              const formattedStats = geocodingMonitoringService.formatStatsForDisplay(data.stats);
              setStats(formattedStats);
              
              const metrics = geocodingMonitoringService.calculatePerformanceMetrics(data.stats);
              setPerformanceMetrics(metrics);
            }
            
            // Mise à jour des providers si disponibles
            if (data.providers) {
              setProvidersStatus(data.providers);
            }
          }
        },
        refreshInterval
      );
      
      monitorRef.current.start();
    }

    return () => {
      if (monitorRef.current) {
        monitorRef.current.stop();
      }
    };
  }, [autoRefresh, refreshInterval]);

  // Chargement initial
  useEffect(() => {
    loadStats();
  }, []);

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  // Calculer la couleur selon le taux de succès
  const getSuccessRateColor = (rate) => {
    const numRate = parseFloat(rate);
    if (numRate >= 95) return 'success';
    if (numRate >= 80) return 'warning';
    return 'error';
  };

  // Calculer la couleur selon le temps de réponse
  const getResponseTimeColor = (time) => {
    const numTime = parseFloat(time);
    if (numTime <= 500) return 'success';
    if (numTime <= 1500) return 'warning';
    return 'error';
  };

  // Obtenir l'icône du provider
  const getProviderIcon = (provider) => {
    switch (provider.toLowerCase()) {
      case 'googlemaps': return '🗺️';
      case 'locationiq': return '🔄';
      case 'nominatim': return '🌍';
      default: return '📍';
    }
  };

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
          {/* Header */}
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          🗺️ Monitoring APIs de Géocodage
        </Typography>
        
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Intervalle</InputLabel>
            <Select
              value={refreshInterval}
              label="Intervalle"
              onChange={(e) => setRefreshInterval(e.target.value)}
            >
              <MenuItem value={2000}>2s</MenuItem>
              <MenuItem value={5000}>5s</MenuItem>
              <MenuItem value={10000}>10s</MenuItem>
              <MenuItem value={30000}>30s</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={toggleAutoRefresh}
                color="primary"
              />
            }
            label="Auto-refresh"
          />
          
          <Tooltip title="Actualiser">
            <IconButton onClick={loadStats} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <TextField
            size="small"
            value={testAddress}
            onChange={(e) => setTestAddress(e.target.value)}
            placeholder="Adresse à tester"
            sx={{ minWidth: 200 }}
          />
          
          <Button
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            onClick={testGeocoding}
            disabled={loading}
          >
            Tester API
          </Button>
          
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RestartAltIcon />}
            onClick={resetStats}
          >
            Reset Stats
          </Button>
        </Box>
      </Box>      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {stats && (
        <>
          {/* Résumé Global */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Résumé Global
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {stats.summary.totalLookups ?? stats.summary.totalRequests}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {stats.summary.totalLookups !== undefined ? 'Total Consultations' : 'Total Requêtes'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {stats.summary.totalSuccesses}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Succès
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="error.main">
                      {stats.summary.totalErrors}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Erreurs
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography 
                      variant="h4" 
                      color={getSuccessRateColor(stats.summary.overallSuccessRate)}
                    >
                      {stats.summary.overallSuccessRate}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Taux de Succès
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Métriques de Performance */}
          {performanceMetrics && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📈 Métriques de Performance
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="success.main">
                        {performanceMetrics.availability}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Disponibilité
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(performanceMetrics.availability)}
                        color="success"
                        sx={{ mt: 1, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="info.main">
                        {performanceMetrics.reliability}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Fiabilité
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(performanceMetrics.reliability)}
                        color="info"
                        sx={{ mt: 1, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="warning.main">
                        {performanceMetrics.activeProviders}/{performanceMetrics.totalProviders}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Providers Actifs
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(performanceMetrics.activeProviders / performanceMetrics.totalProviders) * 100}
                        color="warning"
                        sx={{ mt: 1, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" color="secondary.main">
                        {performanceMetrics.cacheEfficiency}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Efficacité Cache
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={performanceMetrics.cacheEfficiency}
                        color="secondary"
                        sx={{ mt: 1, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Statistiques des Providers */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🗺️ Providers de Géocodage
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Provider</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell align="center">Priorité</TableCell>
                      <TableCell align="center">Requêtes</TableCell>
                      <TableCell align="center">Succès</TableCell>
                      <TableCell align="center">Erreurs</TableCell>
                      <TableCell align="center">Taux Succès</TableCell>
                      <TableCell align="center">Temps Moyen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.formatted.providers.map((provider) => {
                      const providerStatus = providersStatus.find(p => 
                        p.name.toLowerCase() === provider.name.toLowerCase()
                      );
                      
                      const enabled = providerStatus?.enabled ?? true;
                      const onToggle = async (e) => {
                        try {
                          await geocodingMonitoringService.toggleProvider(provider.name.toLowerCase(), e.target.checked);
                          // Refresh providers status after toggle
                          const updated = await geocodingMonitoringService.getProvidersStatus();
                          if (updated.success) setProvidersStatus(updated.data.providers);
                        } catch (err) {
                          console.error('Toggle provider failed', err);
                          setError('Erreur lors de la mise à jour du provider');
                        }
                      };

                      return (
                        <TableRow key={provider.name}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <span>{getProviderIcon(provider.name)}</span>
                              <Typography variant="body2" fontWeight="bold">
                                {provider.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={provider.isActive ? <CheckCircleIcon /> : <ErrorIcon />}
                              label={provider.isActive ? 'Actif' : 'Inactif'}
                              color={provider.isActive ? 'success' : 'default'}
                              size="small"
                            />
                            <Box mt={1}>
                              <FormControlLabel
                                control={<Switch checked={enabled} onChange={onToggle} size="small" />}
                                label={enabled ? 'Activé' : 'Désactivé'}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={providerStatus?.priority || 'N/A'}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">{provider.requests}</TableCell>
                          <TableCell align="center">
                            <Typography color="success.main">
                              {provider.success}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography color="error.main">
                              {provider.errors}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={provider.successRate}
                              color={getSuccessRateColor(provider.successRate)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={provider.avgResponseTime}
                              color={getResponseTimeColor(provider.avgResponseTime)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Cache Statistics */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                💾 Statistiques du Cache
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {stats.formatted.cache.hits}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Cache Hits
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="warning.main">
                      {stats.formatted.cache.misses}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Cache Misses
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {stats.formatted.cache.hitRate}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Taux de Réussite
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="info.main">
                      {stats.formatted.cache.total}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Requêtes Cache
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Efficacité du cache
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={stats.formatted.cache.hitRateNum}
                  color={stats.formatted.cache.hitRateNum > 70 ? 'success' : 'warning'}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                
              </Box>
            </CardContent>
          </Card>

          {/* Détails de Santé des Providers */}
          {providersStatus.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🏥 État de Santé des Providers
                </Typography>
                <Grid container spacing={2}>
                  {providersStatus.map((provider, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" alignItems="center" justifyContent="between" mb={1}>
                            <Typography variant="h6">
                              {getProviderIcon(provider.name)} {provider.name}
                            </Typography>
                            <Chip
                              icon={provider.healthy ? <CheckCircleIcon /> : <ErrorIcon />}
                              label={provider.healthy ? 'Sain' : 'Erreur'}
                              color={provider.healthy ? 'success' : 'error'}
                              size="small"
                            />
                          </Box>
                          
                          <Typography variant="body2" color="textSecondary">
                            Priorité: {provider.priority}
                          </Typography>
                          
                          <Typography variant="body2" color="textSecondary">
                            Activé: {provider.enabled ? 'Oui' : 'Non'}
                          </Typography>
                          {provider.disabledReason && (
                            <Typography variant="body2" color="warning.main">
                              Cooldown: {provider.disabledReason} jusqu'à {provider.disabledUntil ? new Date(provider.disabledUntil).toLocaleTimeString() : ''}
                            </Typography>
                          )}
                          
                          {provider.error && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {provider.error}
                            </Alert>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Timestamp */}
      {stats && (
        <Box mt={3} textAlign="center">
          <Typography variant="caption" color="textSecondary">
            Dernière mise à jour: {stats.formatted.timestamp}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GeocodingMonitoringPage;
